import {
  Card,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import log from 'loglevel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessageSnackbar } from '../../../components/ErrorMessageSnackbar';
import TableCountRow from '../../../components/Table/TableCountRow';
import TableHeadContainer from '../../../components/Table/TableHead';
import {
  CANDIDATE_MATCHES_TABLE_PARAMS,
  MatchStatusEnum,
  RequestQuerySortTypesEnum,
  MATCH_CANDIDATES_STATUS,
} from '../../../consts/enums';
import { useGetMatchCandidates } from '../../../hooks/query/useMatchFlow';
import { usePositionDetailsPageQuery } from '../../../hooks/usePositionDetailsPageQuery';
import { usePromoteMatchCandidate, useRejectMatchCandidates } from '../../../hooks/usePositions';
import { useTablePageParams } from '../../../hooks/useTablePageParams';
import STRING_KEYS from '../../../language/keys';
import { SNACK_BAR_CLOSE_REASON } from '../../TalentsPage/TalentsEdit/utils/consts';
import { CandidateMatchesBulkPanel } from './CandidateMatchesBulkPanel';
import { CandidateMatchItem } from './CandidateMatchItem';
import { CandidateMatchTableFilters } from './CandidateMatchTableFilters';
import { FILTER_QUERY } from './query';
import { style } from './style';

const TAG = '[CANDIDATE_MATCHES_TABLE]';
export const QUERY_PARAMS = {
  TAB: 'tab',
  PAGE: 'page',
};

type CandidateMatchesTableProps = {
  positionId: string,
  accountId: string,
}

export const CandidateMatchesTable = ({
  positionId,
  accountId,
}:CandidateMatchesTableProps):JSX.Element => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { t: translate } = useTranslation();
  const {
    onSortChange,
    activeSort,
    activeFilters,
    onChangeFilter,
  } = useTablePageParams([
    QUERY_PARAMS.TAB,
    QUERY_PARAMS.PAGE,
    FILTER_QUERY.LINKEDIN_URL,
    FILTER_QUERY.RUN_ID,
    FILTER_QUERY.RUN_STATUS,
  ], {
    matchingRank: RequestQuerySortTypesEnum.Ascendant,
  });

  const { onSearchParams, currentPage } = usePositionDetailsPageQuery();

  const {
    data,
    isLoading,
    limit,
    totalCount,
    refetch,
    setEnableQuery,
  } = useGetMatchCandidates(
    positionId,
    accountId,
    Number(currentPage),
    activeSort,
    activeFilters,
  );

  useEffect(() => {
    if (selectedIds.length) {
      setEnableQuery(false);
    } else {
      setEnableQuery(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const promoteMatchCandidate = usePromoteMatchCandidate(() => {
    refetch();
  });

  const rejectMatchCandidate = useRejectMatchCandidates(() => {
    refetch();
  });

  const handleResetSelectAll = () => {
    setSelectedIds([]);
  };

  const headers = [
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.ID,
      value: translate(STRING_KEYS.MATCH_NAME_AND_ID),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.CURRENT_POSITION,
      value: translate(STRING_KEYS.CURRENT_POSITION),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.RANK,
      value: translate(STRING_KEYS.RANK),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.SIMILARITY_SCORE,
      value: translate(STRING_KEYS.SIMILARITY_SCORE),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.WTL_SCORE,
      value: translate(STRING_KEYS.WTL_SCORE),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.LAYOFF_SCORE,
      value: translate(STRING_KEYS.LAYOFF_SCORE),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.TALENT_SEMANTIC_MIN_SCORE,
      value: translate(STRING_KEYS.TALENT_SEMANTIC_SCORE),
      isSortable: true,
    },
    {
      key: CANDIDATE_MATCHES_TABLE_PARAMS.TALENT_SEMANTIC_ABOVE_MIN_COUNT,
      value: translate(STRING_KEYS.TALENT_SEMANTIC_ABOVE_MIN_COUNT),
      isSortable: true,
    },
    {
      key: '',
      value: '',
      isSortable: false,
    },
  ];

  const checkSelectedAll = () => {
    return data.every((candidate: MatchCandidateResponse) => {
      return selectedIds.includes(candidate.id);
    });
  };
  const isAllSelected = checkSelectedAll();

  const unselectIds = (id: string[]) => {
    setSelectedIds((prev) => {
      return prev.filter((selectedId) => !id.includes(selectedId));
    });
  };

  const handleAcceptClick = (id: string[], status: MatchStatusEnum = MatchStatusEnum.published) => {
    promoteMatchCandidate.mutateAsync({
      accountId,
      positionId,
      ids: id,
      status,
    }).then(() => {
      unselectIds(id);
    });
  };

  const handleRejectClick = async (ids: string[]) => {
    try {
      await rejectMatchCandidate.mutateAsync({
        accountId,
        positionId,
        ids,
      });
      
      unselectIds(ids);
    } catch (error) {
      log.error(TAG, 'failed to reject candidate matches', error);
    }
  };

  const handleSnackbarClose = (e: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === SNACK_BAR_CLOSE_REASON.CLICK_AWAY) {
      return;
    }

    rejectMatchCandidate.reset();
  };

  const isItemSelected = (id: string) => {
    return selectedIds.some((selectedId: string) => id === selectedId);
  };

  const handleSetSelectedId = (id: string) => {
    setSelectedIds((prev: string[]) => {
      const arr = [...prev];
      const idx = arr.indexOf(id);
      if (idx === -1) {
        arr.push(id);
      } else {
        arr.splice(idx, 1);
      }

      return arr;
    });
  };

  const handleSelectToggleAll = () => {
    setSelectedIds(() => {
      if (isAllSelected) {
        return [];
      }

      return data.map((candidate) => candidate.id);
    });
  };

  const errorMessage = (status: string, error: MatchCandidateError) => {
    return status === MATCH_CANDIDATES_STATUS.PROMOTION_FAILED ? error.message : '';
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={ headers.length }>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    return data.map((item: MatchCandidateResponse) => {
      return (
        <CandidateMatchItem
          key={ item.id }
          item={ item }
          onAcceptClick={ () => handleAcceptClick([item.id]) }
          onRejectClick={ () => handleRejectClick([item.id]) }
          isSelected={ isItemSelected(item.id) }
          onSelectClick={ () => handleSetSelectedId(item.id) }
          promoteErrorMessage={ errorMessage(item.status, item.error) }
          isPromoting={ item.status === MATCH_CANDIDATES_STATUS.PROMOTION_IN_PROGRESS }
          isPendingPromotion={ item.status === MATCH_CANDIDATES_STATUS.PENDING_PROMOTION }
        />
      );
    });
  };

  const handleChangePage = (
    e: React.MouseEvent<HTMLButtonElement> | null,
    newPageNumber: number,
  ): void => {
    onSearchParams({
      page: (newPageNumber + 1).toString(),
    });
  };

  return (
    <Box
      sx={ style.Box.sx }
    >
      <Card
        sx={ style.boxContainer.sx }
      >
        <CardHeader
          sx={ style.card.cardHeader.sx }
          title={ (
            <Box sx={ style.card.cardHeader.box }>
              <Typography
                variant={ style.card.cardHeader.typography.variant }
                color={ style.card.cardHeader.typography.color }
              >
                {translate(STRING_KEYS.ENGINE_RESULTS)}
              </Typography>
              <Typography
                variant="body2"
                sx={ style.card.cardHeader.typography2.sx }
              >
                {totalCount}
              </Typography>
            </Box>
          ) }
        />
        <CandidateMatchTableFilters
          onFilterChange={ onChangeFilter }
          activeFilters={ activeFilters }
          positionId={ positionId }
          accountId={ accountId }
        />
        <Table size="medium" sx={ style.table.sx }>
          <TableHeadContainer
            headers={ headers }
            order={ activeSort }
            orderHandler={ onSortChange }
            hasSelectAll
            onSelectAll={ handleSelectToggleAll }
            isAllSelected={ isAllSelected && data.length !== 0 }
          />
          <TableCountRow
            count={ data.length }
            text={ translate(STRING_KEYS.TALENTS) }
            colSpan={ headers.length + 1 }
          />
          <TableBody>
            {renderBody()}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={ totalCount }
                page={ currentPage - 1 }
                rowsPerPage={ limit }
                colSpan={ headers.length + 1 }
                onPageChange={ handleChangePage }
                rowsPerPageOptions={ [] }
              />
            </TableRow>
          </TableFooter>
        </Table>
        <ErrorMessageSnackbar
          showError={ rejectMatchCandidate.isError }
          errorMessage={ rejectMatchCandidate.error?.response?.data?.message ||
          translate(STRING_KEYS.MATCH_REJECT_ERROR_MESSAGE) }
          onClose={ handleSnackbarClose }
        />
        <ErrorMessageSnackbar
          showError={  promoteMatchCandidate.isError }
          errorMessage={ promoteMatchCandidate.error?.response?.data?.message ||
          translate(STRING_KEYS.MATCH_PROMOTE_ERROR_MESSAGE) }
          onClose={ handleSnackbarClose }
        />
        {selectedIds.length !== 0 && (
          <CandidateMatchesBulkPanel
            counter={ selectedIds.length }
            onResetSelectedIds={ handleResetSelectAll }
            onAcceptClick={ (status: MatchStatusEnum) => handleAcceptClick(selectedIds, status) }
            onRejectClick={ () => handleRejectClick(selectedIds) }
          />
        )}
      </Card>
    </Box>
  );
};
